import { useCallback, useEffect, useRef, useState } from "react";
import {
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  DatabaseZap,
  FlaskConical,
  History,
  NotebookText,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  TestTubeDiagonal,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import type {
  CleanupJobStatus,
  CleanupJobView,
  CleanupStepView,
  PocOperationalStatus,
  PocTenantView,
  PreImplementationTenantListView,
  TrialTenantView,
} from "@ubisafe/shared";
import { CleanupConfirmationModal } from "../components/CleanupConfirmationModal";
import { CleanupMarkCleanModal } from "../components/CleanupMarkCleanModal";
import { CleanupProgressPanel } from "../components/CleanupProgressPanel";
import { PocTimelineModal } from "../components/PocTimelineModal";
import { TenantUsageFormModal } from "../components/TenantUsageFormModal";
import { api, ApiClientError } from "../lib/api";

const statusDetails: Record<
  PocOperationalStatus,
  { label: string; description: string; icon: typeof CheckCircle2 }
> = {
  AVAILABLE: { label: "Disponível", description: "Pronta para um novo Lead", icon: CheckCircle2 },
  IN_USE: { label: "Em uso", description: "Dentro do prazo previsto", icon: Clock3 },
  OVERDUE: { label: "Prazo excedido", description: "A previsão de término já passou", icon: TriangleAlert },
};

const blockingCleanupStatuses = new Set<CleanupJobStatus>([
  "PENDING",
  "PRECHECKING",
  "INVENTORY",
  "AWAITING_CONFIRMATION",
  "RUNNING",
  "PAUSED_ITEM_ERROR",
  "WAITING_SERVICE",
  "VERIFYING",
  "COMPLETED_WITH_PENDING",
]);

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeZone: "UTC" }).format(
    new Date(`${value}T12:00:00.000Z`),
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

interface PreImplementationPageProps {
  readOnly?: boolean;
}

export function PreImplementationPage({ readOnly = false }: PreImplementationPageProps) {
  const [data, setData] = useState<PreImplementationTenantListView | null>(null);
  const [editingTenant, setEditingTenant] = useState<PocTenantView | TrialTenantView | null>(null);
  const [timelineTenant, setTimelineTenant] = useState<PocTenantView | null>(null);
  const [cleanupTenant, setCleanupTenant] = useState<PocTenantView | null>(null);
  const [markCleanTenant, setMarkCleanTenant] = useState<PocTenantView | null>(null);
  const [cleanupAction, setCleanupAction] = useState<{ jobId: string; key: string } | null>(null);
  const [requestingTenantId, setRequestingTenantId] = useState<string | null>(null);
  const [streamReady, setStreamReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const initialEventCursorRef = useRef<number | null>(null);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError("");
    try {
      const response = await api.listPreImplementationTenants();
      const pocs = await Promise.all(response.pocs.map(async (tenant) => {
        if (!tenant.cleanupJob) return tenant;
        try {
          return {
            ...tenant,
            cleanupJob: await api.getCleanupJob(tenant.identifier, tenant.cleanupJob.id),
          };
        } catch {
          return tenant;
        }
      }));
      if (initialEventCursorRef.current === null) {
        initialEventCursorRef.current = response.eventCursor;
        setStreamReady(true);
      }
      setData({ ...response, pocs });
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível carregar POCs e Trial.");
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!streamReady || initialEventCursorRef.current === null) return;
    const refresh = () => void load(true);
    const fallback = window.setInterval(refresh, 5_000);
    if (typeof EventSource === "undefined") {
      return () => window.clearInterval(fallback);
    }

    const source = new EventSource(
      `/api/tenants/pre-implementation/cleanup-events?after=${initialEventCursorRef.current}`,
      { withCredentials: true },
    );
    source.addEventListener("cleanup-update", refresh);
    return () => {
      window.clearInterval(fallback);
      source.removeEventListener("cleanup-update", refresh);
      source.close();
    };
  }, [load, streamReady]);

  const rememberFocus = () => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  };

  const restoreFocus = () => {
    const target = returnFocusRef.current;
    window.setTimeout(() => target?.focus(), 0);
  };

  const openEditor = (tenant: PocTenantView | TrialTenantView) => {
    rememberFocus();
    setEditingTenant(tenant);
  };

  const closeEditor = () => {
    setEditingTenant(null);
    restoreFocus();
  };

  const openTimeline = (tenant: PocTenantView) => {
    rememberFocus();
    setTimelineTenant(tenant);
  };

  const closeTimeline = () => {
    setTimelineTenant(null);
    restoreFocus();
  };

  const openCleanup = (tenant: PocTenantView) => {
    rememberFocus();
    setCleanupTenant(tenant);
  };

  const closeCleanup = () => {
    setCleanupTenant(null);
    restoreFocus();
  };

  const openMarkClean = (tenant: PocTenantView) => {
    rememberFocus();
    setMarkCleanTenant(tenant);
  };

  const closeMarkClean = () => {
    setMarkCleanTenant(null);
    restoreFocus();
  };

  const replaceTenant = (saved: PocTenantView | TrialTenantView) => {
    setData((current) => {
      if (!current) return current;
      return saved.classification === "POC"
        ? { ...current, pocs: current.pocs.map((tenant) => tenant.identifier === saved.identifier ? saved : tenant) }
        : { ...current, trials: current.trials.map((tenant) => tenant.identifier === saved.identifier ? saved : tenant) };
    });
    setEditingTenant(null);
    restoreFocus();
  };

  const replaceCleanupJob = (identifier: string, job: CleanupJobView) => {
    setData((current) => current ? {
      ...current,
      pocs: current.pocs.map((tenant) => tenant.identifier === identifier ? { ...tenant, cleanupJob: job } : tenant),
    } : current);
  };

  const startedCleanup = (job: CleanupJobView) => {
    if (!cleanupTenant) return;
    replaceCleanupJob(cleanupTenant.identifier, job);
    setCleanupTenant(null);
    restoreFocus();
  };

  const markedClean = (_job: CleanupJobView) => {
    setMarkCleanTenant(null);
    restoreFocus();
    void load(true);
  };

  const runCleanupAction = async (
    tenant: PocTenantView,
    key: string,
    operation: (job: CleanupJobView) => Promise<CleanupJobView>,
    fallbackMessage: string,
  ) => {
    const job = tenant.cleanupJob;
    if (!job || cleanupAction) return;
    setCleanupAction({ jobId: job.id, key });
    setError("");
    try {
      replaceCleanupJob(tenant.identifier, await operation(job));
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : fallbackMessage);
    } finally {
      setCleanupAction(null);
    }
  };

  const confirmCleanupStep = (tenant: PocTenantView, step: CleanupStepView) => {
    void runCleanupAction(
      tenant,
      `confirm:${step.stage}`,
      (job) => api.confirmCleanupStep(tenant.identifier, job.id, step.stage, step.totalCount),
      "Não foi possível confirmar a etapa.",
    );
  };

  const resumeCleanup = (tenant: PocTenantView) => {
    void runCleanupAction(
      tenant,
      "resume-job",
      (job) => api.resumeCleanupJob(tenant.identifier, job.id),
      "Não foi possível retomar a limpeza.",
    );
  };

  const toggleCleanupAutoAdvance = (tenant: PocTenantView, paused: boolean) => {
    void runCleanupAction(
      tenant,
      paused ? "auto-advance:pause" : "auto-advance:resume",
      (job) => api.setCleanupAutoAdvance(tenant.identifier, job.id, paused),
      paused ? "Não foi possível pausar o avanço automático." : "Não foi possível retomar o avanço automático.",
    );
  };

  const retryCleanupItem = (tenant: PocTenantView, itemId: string) => {
    void runCleanupAction(
      tenant,
      `retry-item:${itemId}`,
      (job) => api.retryCleanupItem(tenant.identifier, job.id, itemId),
      "Não foi possível repetir a exclusão do registro.",
    );
  };

  const ignoreCleanupItem = (tenant: PocTenantView, itemId: string) => {
    if (!window.confirm("Ignorar este registro fará o fluxo terminar com pendências. Deseja continuar?")) return;
    void runCleanupAction(
      tenant,
      `ignore-item:${itemId}`,
      (job) => api.ignoreCleanupItem(tenant.identifier, job.id, itemId),
      "Não foi possível ignorar o registro.",
    );
  };

  const retryCleanup = (tenant: PocTenantView) => {
    void runCleanupAction(
      tenant,
      "retry-job",
      (job) => api.retryCleanupJob(tenant.identifier, job.id),
      "Não foi possível retomar a limpeza.",
    );
  };

  const requestServiceRelease = async (tenant: PocTenantView) => {
    if (requestingTenantId) return;
    setRequestingTenantId(tenant.identifier);
    setError("");
    try {
      const response = await api.requestCleanupServiceRelease(tenant.identifier);
      setData((current) => current ? {
        ...current,
        cleanupService: { ...current.cleanupService, status: "REQUESTED", canRequestRelease: false, requestedAt: response.requestedAt },
      } : current);
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível pedir a liberação do serviço.");
    } finally {
      setRequestingTenantId(null);
    }
  };

  return (
    <div className="page-stack pre-implementation-page">
      <section className="page-heading page-heading--split">
        <div>
          <span className="eyebrow">Operação comercial</span>
          <h1>POCs e Trial</h1>
          <p>Acompanhe os ambientes temporários, seus Leads, prazos e responsáveis em uma única visão.</p>
        </div>
        <div className="page-heading__meta">
          {readOnly && (
            <div className="read-only-badge" role="note">
              <ShieldCheck aria-hidden="true" />
              <div><span>Perfil Financeiro</span><strong>Visualização somente leitura</strong></div>
            </div>
          )}
          {data && (
            <div className="sync-summary" aria-label={`Última atualização em ${formatDateTime(data.synchronizedAt)}`}>
              <Clock3 aria-hidden="true" />
              <div><span>Tenants atualizados em</span><strong>{formatDateTime(data.synchronizedAt)}</strong></div>
            </div>
          )}
        </div>
      </section>

      {data?.source === "CONTROLLED" && (
        <div className="controlled-data-banner" role="note">
          <DatabaseZap aria-hidden="true" />
          <div><strong>Dados controlados para validação</strong><span>Os ciclos desta tela são isolados do ambiente real durante os testes locais.</span></div>
        </div>
      )}

      {data && data.cleanupService.executionMode === "DISABLED" && (
        <div className="cleanup-service-banner cleanup-service-banner--unavailable" role="status">
          <TriangleAlert aria-hidden="true" />
          <div>
            <strong>Limpeza real temporariamente desabilitada</strong>
            <span>Nenhuma simulação pode ser iniciada. A integração operacional está sendo validada antes da liberação.</span>
          </div>
        </div>
      )}

      {data && data.cleanupService.executionMode !== "DISABLED" && data.cleanupService.status !== "AVAILABLE" && (
        <div className={`cleanup-service-banner cleanup-service-banner--${data.cleanupService.status.toLowerCase()}`} role="status">
          {data.cleanupService.status === "REQUESTED" ? <Send aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />}
          <div>
            <strong>{data.cleanupService.status === "REQUESTED" ? "Liberação solicitada" : "Serviço de limpeza indisponível"}</strong>
            <span>{data.cleanupService.status === "REQUESTED"
              ? "Um Administrador já foi avisado. A tela será atualizada automaticamente quando o serviço estiver disponível."
              : readOnly
                ? "Novas limpezas estão temporariamente bloqueadas. Consulte o status atual neste painel."
                : "Novas limpezas estão temporariamente bloqueadas. Você pode solicitar a liberação ao Administrador."}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="inline-alert inline-alert--error" role="alert">
          <span>{error}</span>
          {!data && <button onClick={() => void load()}>Tentar novamente</button>}
        </div>
      )}

      {loading && (
        <div className="tenant-grid" aria-label="Carregando POCs e Trial" aria-busy="true">
          {Array.from({ length: 7 }, (_, index) => <div className="tenant-card tenant-card--skeleton" key={index} />)}
        </div>
      )}

      {!loading && !data && (
        <div className="state-panel state-panel--error" role="alert">
          <RefreshCw aria-hidden="true" />
          <h2>Ambientes indisponíveis</h2>
          <p>Não foi possível consultar o snapshot de POCs e Trial.</p>
          <button className="button button--primary" onClick={() => void load()}>Tentar novamente</button>
        </div>
      )}

      {!loading && data && (
        <>
          <section className="usage-section" aria-labelledby="poc-section-title">
            <header className="usage-section__heading">
              <div className="usage-section__icon usage-section__icon--poc"><FlaskConical aria-hidden="true" /></div>
              <div>
                <span className="eyebrow eyebrow--dark">Ambientes temporários</span>
                <h2 id="poc-section-title">POCs</h2>
              </div>
              <span className="usage-section__count">{data.pocs.length} ambientes</span>
            </header>

            {data.pocs.length === 0 ? (
              <div className="usage-empty"><FlaskConical aria-hidden="true" /><span>Nenhum tenant está classificado como POC.</span></div>
            ) : (
              <div className="tenant-grid tenant-grid--usage" aria-live="polite">
                {data.pocs.map((tenant) => {
                  const status = statusDetails[tenant.status];
                  const StatusIcon = status.icon;
                  const cycle = tenant.activeCycle;
                  const cleanupLocksEditing = tenant.cleanupJob ? blockingCleanupStatuses.has(tenant.cleanupJob.status) : false;
                  const cleanupDisabled = data.cleanupService.executionMode === "DISABLED";
                  const serviceUnavailable = data.cleanupService.status !== "AVAILABLE";
                  return (
                    <article className={`tenant-card usage-card usage-card--poc usage-card--${tenant.status.toLowerCase().replace("_", "-")}`} key={tenant.identifier}>
                      <div className="tenant-card__header">
                        <div className="tenant-card__icon usage-card__tenant-icon"><FlaskConical aria-hidden="true" /></div>
                        <span className={`usage-status usage-status--${tenant.status.toLowerCase().replace("_", "-")}`}><StatusIcon aria-hidden="true" /> {status.label}</span>
                      </div>

                                            <div className="tenant-card__identity usage-card__identity"><h3>{tenant.name}</h3><span>Identificador {tenant.identifier}</span></div>
                      <button
                        className="usage-card__timeline-button"
                        onClick={() => openTimeline(tenant)}
                        aria-label={`Ver linha do tempo de ${tenant.name}`}
                        title="Ver linha do tempo"
                      >
                        <History aria-hidden="true" />
                      </button>
                      {cycle ? (
                        <div className="usage-card__content">
                          <div className="usage-card__dates" aria-label={`Período de uso de ${tenant.name}`}>
                            <span><strong>Início:</strong> {formatDate(cycle.startDate)}</span>
                            <span><strong>Previsão:</strong> {formatDate(cycle.expectedEndDate)}</span>
                          </div>
                          <div className="usage-card__lead"><span>Lead</span><strong>{cycle.lead}</strong></div>
                          <dl className="usage-card__details">
                            <div className="usage-card__detail usage-card__detail--commercial"><BriefcaseBusiness aria-hidden="true" /><dt>Comercial responsável</dt><dd>{cycle.commercialOwner}</dd></div>
                            {cycle.notes && <div className="usage-card__notes"><NotebookText aria-hidden="true" /><dt>Anotações</dt><dd tabIndex={0} aria-label={`Anotações de ${tenant.name}`}>{cycle.notes}</dd></div>}
                          </dl>
                        </div>
                      ) : (
                        <div className="usage-card__available"><CheckCircle2 aria-hidden="true" /><div><strong>{status.description}</strong><span>Associe um Lead para iniciar a utilização.</span></div></div>
                      )}

                      {tenant.cleanupJob && (
                        <CleanupProgressPanel
                          job={tenant.cleanupJob}
                          readOnly={readOnly}
                          busyAction={cleanupAction?.jobId === tenant.cleanupJob.id ? cleanupAction.key : null}
                          retryEnabled={!readOnly && !cleanupDisabled}
                          onConfirmStep={(step) => confirmCleanupStep(tenant, step)}
                          onRetryItem={(itemId) => retryCleanupItem(tenant, itemId)}
                          onIgnoreItem={(itemId) => ignoreCleanupItem(tenant, itemId)}
                          onRetryJob={() => retryCleanup(tenant)}
                          onResumeJob={() => resumeCleanup(tenant)}
                          onToggleAutoAdvance={(paused) => toggleCleanupAutoAdvance(tenant, paused)}
                          onMarkClean={() => openMarkClean(tenant)}
                        />
                      )}

                      {!readOnly && (
                        <footer className={`usage-card__actions ${cycle ? "usage-card__actions--cleanup" : "usage-card__actions--single"}`}>
                          {cycle && !tenant.cleanupJob && !cleanupDisabled && !serviceUnavailable && (
                            <button className="danger-button danger-button--small" onClick={() => openCleanup(tenant)}><Trash2 aria-hidden="true" /> Limpar tenant</button>
                          )}
                          {cycle && !tenant.cleanupJob && !cleanupDisabled && serviceUnavailable && (
                            <button
                              className="secondary-button secondary-button--small cleanup-service-request-button"
                              onClick={() => void requestServiceRelease(tenant)}
                              disabled={!data.cleanupService.canRequestRelease || Boolean(requestingTenantId)}
                            >
                              <Send aria-hidden="true" />
                              {data.cleanupService.status === "REQUESTED" ? "Liberação solicitada" : requestingTenantId === tenant.identifier ? "Solicitando…" : "Pedir liberação"}
                            </button>
                          )}
                          {cycle && cleanupDisabled && (
                            <span className="cleanup-disabled-note"><ShieldCheck aria-hidden="true" /> Limpeza real em validação</span>
                          )}
                          <button
                            className="primary-button usage-card__primary-action"
                            onClick={() => openEditor(tenant)}
                            disabled={cleanupLocksEditing}
                            title={cleanupLocksEditing ? "A edição fica bloqueada durante o Serviço de limpeza." : undefined}
                          >
                            {cycle ? <Pencil aria-hidden="true" /> : <Plus aria-hidden="true" />}
                            {cycle ? "Editar uso" : "Iniciar uso"}
                          </button>
                        </footer>
                      )}
                      {!readOnly && cleanupLocksEditing && <p className="cleanup-edit-lock"><ShieldCheck aria-hidden="true" /> Edição bloqueada somente durante esta limpeza.</p>}
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section className="usage-section usage-section--trial" aria-labelledby="trial-section-title">
            <header className="usage-section__heading">
              <div className="usage-section__icon usage-section__icon--trial"><TestTubeDiagonal aria-hidden="true" /></div>
              <div><span className="eyebrow eyebrow--dark">Ambiente flexível</span><h2 id="trial-section-title">Trial</h2><p>Informações correntes livremente editáveis, sem ciclo, estado ou histórico.</p></div>
              <span className="usage-section__count">{data.trials.length} ambiente</span>
            </header>

            <div className="tenant-grid tenant-grid--trial">
              {data.trials.map((tenant) => (
                <article className="tenant-card usage-card usage-card--trial" key={tenant.identifier}>
                  <div className="tenant-card__header">
                    <div className="tenant-card__icon usage-card__tenant-icon"><TestTubeDiagonal aria-hidden="true" /></div>
                    <span className="usage-status usage-status--trial"><TestTubeDiagonal aria-hidden="true" /> Trial</span>
                  </div>
                  <div className="tenant-card__identity usage-card__identity"><h3>{tenant.name}</h3><span>Identificador {tenant.identifier}</span></div>
                  {tenant.state ? (
                    <div className="usage-card__content">
                      {(tenant.state.startDate || tenant.state.expectedEndDate) && (
                        <div className="usage-card__dates" aria-label={`Período de uso de ${tenant.name}`}>
                          {tenant.state.startDate && <span><strong>Início:</strong> {formatDate(tenant.state.startDate)}</span>}
                          {tenant.state.expectedEndDate && <span><strong>Previsão:</strong> {formatDate(tenant.state.expectedEndDate)}</span>}
                        </div>
                      )}
                      {tenant.state.lead && <div className="usage-card__lead"><span>Lead</span><strong>{tenant.state.lead}</strong></div>}
                      <dl className="usage-card__details">
                        {tenant.state.commercialOwner && <div className="usage-card__detail usage-card__detail--commercial"><BriefcaseBusiness aria-hidden="true" /><dt>Comercial responsável</dt><dd>{tenant.state.commercialOwner}</dd></div>}
                        {tenant.state.notes && <div className="usage-card__notes"><NotebookText aria-hidden="true" /><dt>Anotações</dt><dd tabIndex={0} aria-label={`Anotações de ${tenant.name}`}>{tenant.state.notes}</dd></div>}
                      </dl>
                    </div>
                  ) : (
                    <div className="usage-card__available usage-card__available--trial"><TestTubeDiagonal aria-hidden="true" /><div><strong>Sem informações registradas</strong><span>Preencha os dados atuais quando necessário.</span></div></div>
                  )}
                  {!readOnly && (
                    <footer className="usage-card__actions usage-card__actions--single"><button className="primary-button usage-card__primary-action" onClick={() => openEditor(tenant)}><Pencil aria-hidden="true" /> Editar informações</button></footer>
                  )}
                </article>
              ))}
            </div>
          </section>
        </>
      )}

      {!readOnly && editingTenant && <TenantUsageFormModal tenant={editingTenant} onClose={closeEditor} onSaved={replaceTenant} />}
      {timelineTenant && <PocTimelineModal tenant={timelineTenant} onClose={closeTimeline} />}
      {!readOnly && cleanupTenant && <CleanupConfirmationModal tenant={cleanupTenant} onClose={closeCleanup} onStarted={startedCleanup} />}
      {!readOnly && markCleanTenant?.cleanupJob && (
        <CleanupMarkCleanModal
          tenant={markCleanTenant}
          job={markCleanTenant.cleanupJob}
          onClose={closeMarkClean}
          onSaved={markedClean}
        />
      )}
    </div>
  );
}
